import React, { useState } from "react";
import { Calendar as CalIcon, GitCompareArrows, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { RANGE_PRESETS, COMPARE_OPTIONS } from "./usePerformance";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

export function RangeSelector({ state, periodLabel, onChange }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(state.from || "");
  const [to, setTo] = useState(state.to || "");
  const activePreset = RANGE_PRESETS.find((p) => p.id === state.range);
  const label = state.from ? periodLabel || `${state.from} → ${state.to}` : activePreset?.label || periodLabel;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 h-10" data-testid="perf-range-btn">
          <CalIcon className="h-4 w-4 text-muted-foreground" />
          <span className="max-w-[210px] truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-2" data-testid="perf-range-menu">
        <div className="grid grid-cols-2 gap-1">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => { onChange({ range: p.id, from: null, to: null }); setOpen(false); }}
              data-testid={`perf-range-${p.id}`}
              className={cn(
                "flex items-center justify-between rounded-lg px-2.5 py-2 text-xs text-left transition-colors",
                state.range === p.id ? "bg-accent text-foreground font-medium" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
              )}
            >
              {p.label}
              {state.range === p.id && <Check className="h-3 w-3" />}
            </button>
          ))}
        </div>
        <div className="border-t border-border mt-2 pt-2 space-y-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold px-1">{tfr("Période personnalisée")}</p>
          <div className="flex items-center gap-2">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 text-xs" aria-label={tfr("Date de début")} data-testid="perf-range-from" />
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-xs" aria-label={tfr("Date de fin")} data-testid="perf-range-to" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>{tfr("Annuler")}</Button>
            <Button size="sm" disabled={!from || !to || to < from}
              onClick={() => { onChange({ from, to, range: null }); setOpen(false); }}
              data-testid="perf-range-apply">
              Appliquer
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function CompareSelector({ state, onChange }) {
  const [open, setOpen] = useState(false);
  const [cf, setCf] = useState(state.compareFrom || "");
  const [ct, setCt] = useState(state.compareTo || "");
  const active = COMPARE_OPTIONS.find((o) => o.id === state.compare);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 h-10" data-testid="perf-compare-btn">
          <GitCompareArrows className="h-4 w-4 text-muted-foreground" />
          <span className="max-w-[190px] truncate">{state.compare === "none" ? tfr("Sans comparaison") : `${tfr("Comparer :")} ${active?.label || ""}`}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[280px] p-2" data-testid="perf-compare-menu">
        <div className="space-y-1">
          {COMPARE_OPTIONS.map((o) => (
            <button key={o.id}
              onClick={() => { if (o.id !== "custom") { onChange({ compare: o.id }); setOpen(false); } else onChange({ compare: "custom" }); }}
              data-testid={`perf-compare-${o.id}`}
              className={cn("flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-xs text-left transition-colors",
                state.compare === o.id ? "bg-accent text-foreground font-medium" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground")}>
              {o.label}
              {state.compare === o.id && <Check className="h-3 w-3" />}
            </button>
          ))}
        </div>
        {state.compare === "custom" && (
          <div className="border-t border-border mt-2 pt-2 space-y-2">
            <div className="flex items-center gap-2">
              <Input type="date" value={cf} onChange={(e) => setCf(e.target.value)} className="h-9 text-xs" aria-label={tfr("Comparaison : début")} data-testid="perf-compare-from" />
              <Input type="date" value={ct} onChange={(e) => setCt(e.target.value)} className="h-9 text-xs" aria-label="Comparaison : fin" data-testid="perf-compare-to" />
            </div>
            <div className="flex justify-end">
              <Button size="sm" disabled={!cf || !ct || ct < cf}
                onClick={() => { onChange({ compare: "custom", compareFrom: cf, compareTo: ct }); setOpen(false); }}
                data-testid="perf-compare-apply">Appliquer</Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
